import React, { useState } from 'react';
import styled from 'styled-components';

import { getChainPrivateApi } from 'api/apiCoreSdk';
import Button from 'components/basic/Button/Button';
import IconSvgComponent from 'components/basic/icons/IconSvgComponent';
import ModalContent from 'components/basic/modal/components/ModalContent';
import ModalFooterButtons from 'components/basic/modal/components/ModalFooterButtons';
import SimpleModalOrMobilePage from 'components/basic/modal/SimpleModalOrMobilePage';
import ErrorPanel from 'components/basic/panels/ErrorPanel';
import { useActiveWindowInterval } from 'components/hooks';
import { KATANA_PERPS } from 'config/constants';
import { makeEthersErrorHumanRedable } from 'lib/ethereum/ethersErrors';
import { requestUnlockAuthSignature } from 'lib/ethereum/wallets/assignWallets';
import { generateQrCodeFromNumbers } from 'lib/qrCode/generateQr';
import { skipDesktopQRConnectModal } from 'reduxState/exchange/userGuideReducer';
import { staticDispatch } from 'reduxState/store';
import { useSelector } from 'reduxState/storeCore';

import { generateSyncQrCompressedNumsPayload } from '../qrSyncPayload';
import QrSyncSteps from '../QrSyncSteps';

const Heading = styled.h1`
  padding-top: 40px;
  margin: 0;
  line-height: normal;
`;

const SubHeading = styled.div`
  color: var(--gray4c);
  font-size: var(--medium);
  font-weight: normal;
  line-height: 1.4;
  padding: 12px 8px 8px 8px;
`;

const GenerateWrap = styled.div`
  margin: 8px 0;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GeneratedWrap = styled.div`
  min-height: calc(220px + 16px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IconWrap = styled.div`
  position: absolute;
  opacity: 0.15;
`;

function useDetectNewDelegatedKey({
  searchAddress,
  onFound,
}: {
  searchAddress?: string;
  onFound: () => void;
}) {
  const wallet = useSelector(state => state.wallet);

  const [delegatedKeysIteration, setDelegatedKeysIteration] = useState(0);

  useActiveWindowInterval(
    async () => {
      if (!searchAddress) {
        return;
      }
      setDelegatedKeysIteration(count => count + 1);
      const keysReponse = await getChainPrivateApi().getInternalDelegatedKeys(
        wallet.walletAddress
      );
      const exists = keysReponse.keys.some(
        key => key.delegatedKey.toLowerCase() === searchAddress.toLowerCase()
      );
      if (exists) {
        onFound();
      }
    },
    3_000 + delegatedKeysIteration * 500
  );
}

export default function QrSyncModalDesktop({
  doNotShowAgainOption,
  onClose,
}: {
  onClose: () => void;
  doNotShowAgainOption: boolean;
}) {
  const wallet = useSelector(state => state.wallet);
  const bankrollCodeAppliedMessage = useSelector(
    state => state.currentUserData.userBankroll.bankrollCodeAppliedMessage
  );

  const [qrCode, setQrCode] = useState('');
  const [qrKeyAddress, setQKeyAddress] = useState<string>();
  const [generating, setGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useDetectNewDelegatedKey({
    searchAddress: qrKeyAddress,
    onFound: onClose,
  });

  const handleGenerate = async () => {
    const { signature, delegatedKeyParams, nonce } =
      await requestUnlockAuthSignature(wallet, true);

    const delegatedKeyAddress = delegatedKeyParams?.delegatedHDWallet.address;

    const delegatedPrivateKey =
      delegatedKeyParams?.delegatedHDWallet.privateKey;

    if (!delegatedPrivateKey) {
      throw new Error('Delegated key generation error');
    }
    const payload = generateSyncQrCompressedNumsPayload({
      authorizationSignature: signature,
      custodyWalletAddress: wallet.walletAddress,
      delegatedPrivateKey,
      nonce,
    });
    const qrCodeUrl = await generateQrCodeFromNumbers(payload, {
      errorCorrectionLevel: 'low',
    });
    setQrCode(qrCodeUrl);
    setQKeyAddress(delegatedKeyAddress);
  };

  const handleGenerateAndErrorHandle = () => {
    setErrorMessage('');
    setGenerating(true);
    handleGenerate()
      .catch(error => {
        console.warn(error);
        setErrorMessage(makeEthersErrorHumanRedable(error));
      })
      .finally(() => setGenerating(false));
  };

  if (bankrollCodeAppliedMessage) {
    // Wait until user close Welcome bonus modal
    return null;
  }

  return (
    <SimpleModalOrMobilePage onClose={onClose} size="medium" padding="0">
      <ModalContent>
        <div style={{ textAlign: 'center' }}>
          <Heading>
            Trade anytime, anywhere with
            <br />
            {KATANA_PERPS} Connect for mobile
          </Heading>
          <SubHeading>Link your wallet using this QR Code:</SubHeading>
          {!qrCode && (
            <GenerateWrap>
              <IconWrap>
                <IconSvgComponent icon="qrCodeWhite" size="196px" />
              </IconWrap>
              <Button
                onClick={handleGenerateAndErrorHandle}
                disabled={generating}
                inline
              >
                Generate QR Code
              </Button>
            </GenerateWrap>
          )}
          {errorMessage && <ErrorPanel marginTop={8} message={errorMessage} />}
          {qrCode && (
            <GeneratedWrap>
              <img src={qrCode} style={{ borderRadius: 5 }} />
            </GeneratedWrap>
          )}
          <QrSyncSteps isDesktop />
        </div>
      </ModalContent>
      {doNotShowAgainOption && (
        <ModalFooterButtons
          confirmButtonProps={{
            onClick: () => {
              staticDispatch(skipDesktopQRConnectModal());
              onClose();
            },
            bordered: true,
            widePadding: true,
            inline: true,
            children: "Don't show again",
          }}
        />
      )}
    </SimpleModalOrMobilePage>
  );
}
